import { useEffect, useRef } from "react";
import { HomeIcon } from "components/Icons";
import { useFormContext } from "react-hook-form";

export const AutoCompleteInput = ({
  searchType,
  onPlaceSelect,
  setPlaceAutocomplete,
  setCountryState,
  setCountryCity,
  setCountryZipcode,
  selectedPlace,
  setSelectedPlace,
  setAptOrSuite,
  onDropdownStateChange,
  setManualAddress,
  hasError = false,
}) => {
  const inputRef = useRef(null);
  const { setValue, trigger } = useFormContext();

  // Keep latest callbacks in a ref so the effect can depend only on searchType
  // and avoid "Maximum update depth exceeded" when setPlaceAutocomplete causes re-renders.
  const callbacksRef = useRef({
    onPlaceSelect,
    setPlaceAutocomplete,
    setCountryState,
    setCountryCity,
    setCountryZipcode,
    setSelectedPlace,
    setAptOrSuite,
    onDropdownStateChange,
    setManualAddress,
    setValue,
    trigger,
  });
  callbacksRef.current = {
    onPlaceSelect,
    setPlaceAutocomplete,
    setCountryState,
    setCountryCity,
    setCountryZipcode,
    setSelectedPlace,
    setAptOrSuite,
    onDropdownStateChange,
    setManualAddress,
    setValue,
    trigger,
  };

  // Stable key so effect doesn't re-run when parent passes a new array reference every render (e.g. searchType={['subpremise','geocode']}).
  const searchTypeKey =
    typeof searchType === "string"
      ? searchType
      : Array.isArray(searchType)
        ? searchType.filter((t) => typeof t === "string").sort().join(",")
        : "";

  useEffect(() => {
    if (!window.google || !inputRef.current) return;

    const raw = Array.isArray(searchType) ? searchType : [searchType];
    const types = raw.filter((t) => typeof t === "string");
    const typesOption = types.length ? types : ["address"];
    const autocomplete = new window.google.maps.places.Autocomplete(
      inputRef.current,
      {
        fields: ["address_component", "geometry", "name", "formatted_address"],
        types: typesOption,
        componentRestrictions: { country: "us" },
      }
    );

    autocomplete.addListener("place_changed", () => {
      const place = autocomplete.getPlace();
      const cbs = callbacksRef.current;

      if (place.geometry) {
        cbs.onPlaceSelect?.(place);
      }

      let city, state, zipCode, subpremise, streetNumber, route, streetAddress;

      if (!place.address_components) {
        console.error("No address components found.");
        return;
      }

      place.address_components.forEach((component) => {
        const componentTypes = component.types;
        if (componentTypes.includes("locality")) {
          city = component.long_name;
        } else if (componentTypes.includes("administrative_area_level_1")) {
          state = component.short_name;
        } else if (componentTypes.includes("postal_code")) {
          zipCode = component.long_name;
        } else if (componentTypes.includes("subpremise")) {
          subpremise = component.long_name;
        } else if (componentTypes.includes("street_number")) {
          streetNumber = component.long_name;
        } else if (componentTypes.includes("route")) {
          route = component.long_name;
        }
      });

      streetAddress = [streetNumber, route].filter(Boolean).join(" ");

      cbs.setCountryState?.(state || "");
      cbs.setCountryCity?.(city || "");
      cbs.setCountryZipcode?.(zipCode || "");
      cbs.setAptOrSuite?.(subpremise || "");
      cbs.setSelectedPlace?.({ name: place.name });
      cbs.setManualAddress?.(place.formatted_address || place.name);

      cbs.setValue?.("city", city || "", { shouldValidate: true });
      cbs.setValue?.("state", state || "", { shouldValidate: true });
      cbs.setValue?.("zipCode", zipCode || "", { shouldValidate: true });
      cbs.setValue?.("suiteOrApt", subpremise || "", { shouldValidate: true });
      cbs.setValue?.("streetName", streetAddress || place.name, { shouldValidate: true });
      cbs.setValue?.("address", { name: place.name || "" }, { shouldValidate: true });

      setTimeout(() => {
        cbs.trigger?.(["city", "state", "zipCode", "streetName"]);
      }, 100);

      cbs.onDropdownStateChange?.(false);
    });

    callbacksRef.current.setPlaceAutocomplete?.(autocomplete);
    // searchTypeKey is the stable representation of searchType (see comment above).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTypeKey]);

  const handleFocus = () => {
    if (inputRef.current.value.trim().length > 0) {
      onDropdownStateChange(true);
    }
  };

  const handleBlur = () => {
    setTimeout(() => {
      onDropdownStateChange(false);
    }, 200);
  };

  useEffect(() => {
    if (selectedPlace && selectedPlace.name) {
      inputRef.current.value = selectedPlace.name;
    }
  }, [selectedPlace]);

  useEffect(() => {
    const handleInput = () => {
      const currentValue = inputRef.current.value;
      setManualAddress(currentValue);
      if (selectedPlace && currentValue.trim() !== selectedPlace.name) {
        setSelectedPlace({ name: null });
      }
      onDropdownStateChange(currentValue.trim().length > 0);
    };

    const inputElement = inputRef.current;
    if (inputElement) {
      inputElement.addEventListener("input", handleInput);
    }
    return () => {
      if (inputElement) {
        inputElement.removeEventListener("input", handleInput);
      }
    };
  }, [
    selectedPlace,
    setSelectedPlace,
    setManualAddress,
    onDropdownStateChange,
  ]);

  return (
    <div className="flex gap-[20px] !w-full">
      <div className="flex flex-col relative !w-full lg:flex-row justify-between items-center gap-[20px]">
        <div className="absolute top-[-10px] left-[10px] w-[100px] flex items-center bg-white z-1 gap-x-2 justify-center">
          <HomeIcon className="!text-transparent !cursor-pointer w-[16px] h-[16px]" />
          <label className="state-heading text-[14px]">Address *</label>
        </div>
        <input
          className={`map-input-location ${hasError ? '!border-red-500' : ''}`}
          type="text"
          ref={inputRef}
          onFocus={handleFocus}
          onBlur={handleBlur}
        />
      </div>
    </div>
  );
};